import { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosConfig";
import { Card, Table, Input, Typography, Spin, Alert, App } from "antd";
import { SearchOutlined, ApiOutlined } from "@ant-design/icons";
import DashboardLayout from "./DashboardLayout";

const { Title } = Typography;

export default function ApiCollection() {
  const { message } = App.useApp();
  const [apis, setApis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchApis();
  }, []);

  const fetchApis = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/api/apiCollection");
      setApis(response.data);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Failed to fetch API collection";
      setError(errorMessage);
      message.error(errorMessage, 4);
    } finally {
      setLoading(false);
    }
  };

  const filteredApis = apis.filter(
    (api) =>
      (api.name?.toLowerCase() || "").includes(search.toLowerCase()) ||
      (api.serverUrl?.toLowerCase() || "").includes(search.toLowerCase())
  );

  const columns = [
    {
      title: "API ID",
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => a.id - b.id,
      width: 100,
    },
    {
      title: "API Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
    },
    {
      title: "API URL",
      dataIndex: "serverUrl",
      key: "serverUrl",
      ellipsis: true,
      render: (text) => (
        <span style={{ fontFamily: "monospace", fontSize: "13px" }}>
          {text}
        </span>
      ),
    },
  ];

  return (
    <DashboardLayout title="API Collection">
      <Card
        style={{
          borderRadius: 12,
          boxShadow: "0 4px 16px rgba(0, 102, 255, 0.08)",
          border: "1px solid #e1e8f0",
        }}
      >
        <div style={{ marginBottom: "24px" }}>
          <Title
            level={2}
            style={{
              textAlign: "center",
              marginBottom: "24px",
              background: "linear-gradient(135deg, #0066ff 0%, #0052cc 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              fontWeight: 700,
            }}
          >
            <ApiOutlined /> API Collection
          </Title>

          <Input
            placeholder="Search by API name or URL"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: "400px" }}
            allowClear
            size="large"
          />
        </div>

        {error && (
          <Alert
            title="Error"
            description={error}
            type="error"
            showIcon
            closable
            onClose={() => setError("")}
            style={{ marginBottom: "24px", borderRadius: 8 }}
          />
        )}

        <Spin spinning={loading}>
          <Table
            dataSource={filteredApis}
            columns={columns}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} APIs`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            locale={{
              emptyText: "No APIs found",
            }}
          />
        </Spin>
      </Card>
    </DashboardLayout>
  );
}
